/* Suivi des rendus + correction IA d'un devoir (prof) — roadmap-v1 blocs 05 + 07. */

import { useEffect, useRef, useState } from 'react';
import { Sparkle, Check } from '@phosphor-icons/react';
import { useNavigate, useParams } from '../lib/routing';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import GraphiePanel, { type GraphieAnalysisView, type GraphieTexteView } from '../components/graphie-panel';
import { numericTotals } from '../lib/grading-scale';
import { LetterSelect, type LetterOption } from '../components/letter-select';

interface Row {
  classroom_student_id: string;
  first_name: string;
  last_name: string;
  status: string;
  submitted_at: string | null;
  photos: string[];
}
interface CorrItem {
  sectionIndex: number;
  sectionTitle: string;
  score: number;
  max: number;
  feedback: string;
  unread?: boolean;
  letter?: string;
  scaleId?: string;
  letterLabel?: string;
  letterOptions?: LetterOption[];
}
interface CorrResult {
  totalScore: number;
  totalMax: number;
  overallFeedback: string;
  items: CorrItem[];
  model: string;
}
interface CorrRow {
  result: CorrResult;
  teacher_override: CorrResult | null;
  published_to_student: boolean;
}

const STATUS_LABEL: Record<string, string> = { not_started: 'À faire', draft: 'En cours', submitted: 'Rendu' };
const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = { not_started: 'outline', draft: 'secondary', submitted: 'default' };

/* Une section non lue ou notée par lettres sort du total numérique. */
const totals = numericTotals;

function unreadLabel(count: number): string {
  const s = count > 1 ? 's' : '';
  return `${count} section${s} non lue${s} à corriger`;
}

export default function TeacherAssignmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [title, setTitle] = useState('Devoir');
  const [format, setFormat] = useState<string>('closed');
  const [recopieTexteId, setRecopieTexteId] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<CorrResult | null>(null);
  const [published, setPublished] = useState(false);
  const [hasOverride, setHasOverride] = useState(false);
  const [confirmingRecorrect, setConfirmingRecorrect] = useState(false);
  const [busy, setBusy] = useState(false);

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSuccessMsg(null), 3000);
  };

  useEffect(() => {
    if (!id) return;
    Promise.all([api.assignments.get.query({ id }), api.assignments.submissions.query({ id })])
      .then(([a, s]) => {
        const doc = a as { title?: string; format?: string; recopie_texte_id?: string | null } | null;
        if (doc?.title) setTitle(doc.title);
        if (doc?.format) setFormat(doc.format);
        setRecopieTexteId(doc?.recopie_texte_id ?? null);
        setRows(s as Row[]);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const load = (row: CorrRow | null) => {
    const eff = row?.teacher_override ?? row?.result ?? null;
    setDraft(eff ? { ...eff, items: eff.items.map((i) => ({ ...i })) } : null);
    setPublished(row?.published_to_student ?? false);
    setHasOverride(Boolean(row?.teacher_override));
  };

  const toggleStudent = (studentId: string) => {
    if (openId === studentId) {
      setOpenId(null);
      return;
    }
    void openStudent(studentId);
  };

  const openStudent = async (studentId: string) => {
    if (!id) return;
    setOpenId(studentId);
    setDraft(null);
    setConfirmingRecorrect(false);
    setError(null);
    try {
      const c = await api.assignments.correction.get.query({ assignmentId: id, classroomStudentId: studentId });
      load(c as CorrRow | null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const runCorrection = async (studentId: string) => {
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const c = await api.assignments.correction.run.mutate({ assignmentId: id, classroomStudentId: studentId });
      load(c as CorrRow);
      setConfirmingRecorrect(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const requestRecorrect = (studentId: string) => {
    if (hasOverride) {
      setConfirmingRecorrect(true);
      return;
    }
    void runCorrection(studentId);
  };

  const save = async (studentId: string) => {
    if (!id || !draft) return;
    setBusy(true);
    try {
      await api.assignments.correction.override.mutate({
        assignmentId: id,
        classroomStudentId: studentId,
        override: { ...draft, ...totals(draft.items) },
      });
      showSuccess('Correction enregistrée.');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (studentId: string) => {
    if (!id) return;
    const unread = (draft?.items ?? []).filter((it) => it.unread).length;
    if (!published && unread > 0) {
      setError(`${unreadLabel(unread)} — publication refusée.`);
      return;
    }
    setBusy(true);
    try {
      await api.assignments.correction.publish.mutate({ assignmentId: id, classroomStudentId: studentId, published: !published });
      setPublished((p) => !p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const setItem = (idx: number, patch: Partial<CorrItem>) =>
    setDraft((d) => (d ? { ...d, items: d.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) } : d));

  const submitted = rows.filter((r) => r.status === 'submitted').length;
  const draftTotals = draft ? totals(draft.items) : null;
  const unreadCount = draft ? draft.items.filter((it) => it.unread).length : 0;
  const publishBlocked = !published && unreadCount > 0;

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Header />
      <HeaderContent width="md" showBurger onBack={() => navigate(-1)} backLabel="Retour">
        <h1 className={HEADER_TITLE_CLASS}>{title}</h1>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
        {successMsg && (
          <div className="flex items-center gap-2 mb-4 px-4 py-2.5 rounded-card bg-[var(--naia-green-bg)] border border-[var(--naia-green-deep)]/20 text-[var(--naia-green-deep)] text-[13px] font-semibold">
            <Check size={15} weight="bold" />
            {successMsg}
          </div>
        )}
        {error && <Alert className="mb-4">{error}</Alert>}

        {!loading && rows.length > 0 && (
          <div className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-card bg-card border border-border">
            <span className="text-[13px] font-semibold text-foreground tabular-nums">{submitted}/{rows.length} rendu{rows.length > 1 ? 's' : ''}</span>
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: rows.length ? `${(submitted / rows.length) * 100}%` : '0%' }} />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[58px] rounded-card" />)}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-[13px] text-muted-foreground py-4">Aucun élève n'a ce devoir pour l'instant.</p>
        ) : (
          <div className="rounded-card border border-border overflow-hidden shadow-card">
            {rows.map((r, i) => (
              <div key={r.classroom_student_id}>
                {i > 0 && <Separator />}
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[14px] text-foreground">{r.first_name} {r.last_name}</div>
                      {r.submitted_at && (
                        <div className="text-[12px] text-muted-foreground mt-0.5">
                          Rendu le {new Date(r.submitted_at).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                    <Badge variant={STATUS_VARIANT[r.status] ?? 'outline'}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
                    {r.status === 'submitted' && format !== 'interactive' && (
                      <Button variant="secondary" size="sm" onClick={() => toggleStudent(r.classroom_student_id)}>
                        {openId === r.classroom_student_id ? 'Fermer' : recopieTexteId ? 'Graphie' : 'Correction'}
                      </Button>
                    )}
                    {format === 'interactive' && id && (
                      <ParcoursResultView assignmentId={id} classroomStudentId={r.classroom_student_id} />
                    )}
                  </div>

                  {r.photos.length > 0 && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {r.photos.map((url, pi) => (
                        <a key={pi} href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={`Rendu — photo ${pi + 1}`} className="w-[72px] h-[72px] object-cover rounded-lg border border-border" />
                        </a>
                      ))}
                    </div>
                  )}

                  {openId === r.classroom_student_id && !recopieTexteId && (
                    <div className="mt-3 pt-3 border-t border-border flex flex-col gap-3">
                      {!draft ? (
                        <Button disabled={busy} onClick={() => void runCorrection(r.classroom_student_id)}>
                          <Sparkle size={15} weight="fill" className="mr-1.5" />
                          {busy ? 'Correction en cours…' : 'Corriger avec Naïa'}
                        </Button>
                      ) : (
                        <>
                          <div className="text-[13px] font-extrabold text-foreground tabular-nums">
                            {draftTotals!.totalMax > 0 ? `Total : ${draftTotals!.totalScore} / ${draftTotals!.totalMax}` : 'Notation par lettres'}
                          </div>
                          {draft.items.map((it, i) => (
                            <div key={i} className="border border-border rounded-card p-3 flex flex-col gap-2">
                              <div className="font-semibold text-[13.5px] text-foreground">{it.sectionTitle || `Section ${it.sectionIndex + 1}`}</div>
                              {it.unread ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  <Badge variant="secondary">Non lue</Badge>
                                  <span className="text-[12px] text-muted-foreground">Section illisible — à corriger manuellement.</span>
                                  <Button variant="secondary" size="sm" disabled={busy} onClick={() => setItem(i, { unread: false })}>
                                    Corriger manuellement
                                  </Button>
                                </div>
                              ) : (
                                it.scaleId !== undefined ? (
                                  <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                                    <span>Lettre :</span>
                                    <LetterSelect letter={it.letter} options={it.letterOptions ?? []} onPick={(patch) => setItem(i, patch)} />
                                  </div>
                                ) : (
                                <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                                  <span>Note :</span>
                                  <Input
                                    type="number"
                                    value={it.score}
                                    min={0}
                                    max={it.max}
                                    step={0.5}
                                    onChange={(e) => setItem(i, { score: Number(e.target.value) })}
                                    className="w-16 h-8 text-center"
                                  />
                                  <span>/ {it.max}</span>
                                </div>
                                )
                              )}
                              <Textarea
                                value={it.feedback}
                                onChange={(e) => setItem(i, { feedback: e.target.value })}
                                rows={2}
                                className="text-[13px] resize-none"
                              />
                            </div>
                          ))}
                          <div className="flex flex-col gap-1.5">
                            <span className="text-[12px] font-semibold text-muted-foreground">Appréciation générale</span>
                            <Textarea
                              value={draft.overallFeedback}
                              onChange={(e) => setDraft((d) => (d ? { ...d, overallFeedback: e.target.value } : d))}
                              rows={2}
                              className="text-[13px] resize-none"
                            />
                          </div>
                          {confirmingRecorrect ? (
                            <div className="flex flex-col gap-2 rounded-card border border-border p-3">
                              <p className="m-0 text-[13px] text-foreground">
                                Une nouvelle correction par l'IA remplacera votre correction enregistrée et dépubliera la copie.
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                <Button size="sm" disabled={busy} onClick={() => void runCorrection(r.classroom_student_id)}>
                                  {busy ? 'Correction en cours…' : 'Confirmer la re-correction'}
                                </Button>
                                <Button variant="secondary" size="sm" disabled={busy} onClick={() => setConfirmingRecorrect(false)}>
                                  Annuler
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2 flex-wrap">
                              <Button variant="secondary" size="sm" disabled={busy} onClick={() => requestRecorrect(r.classroom_student_id)}>Re-corriger</Button>
                              <Button size="sm" disabled={busy} onClick={() => void save(r.classroom_student_id)}>Enregistrer</Button>
                              <Button variant={published ? 'default' : 'secondary'} size="sm" disabled={busy || publishBlocked} onClick={() => void togglePublish(r.classroom_student_id)}>
                                {published ? <><Check size={13} weight="bold" className="mr-1" />Publié à l'élève</> : "Publier à l'élève"}
                              </Button>
                            </div>
                          )}
                          {publishBlocked && (
                            <p className="text-[11.5px] text-muted-foreground">{unreadLabel(unreadCount)} — corrigez la section à la main avant de publier.</p>
                          )}
                          <p className="text-[11.5px] text-muted-foreground">Proposition IA — relire et valider avant publication.</p>
                        </>
                      )}
                    </div>
                  )}

                  {openId === r.classroom_student_id && id && (
                    <GraphiePanel
                      key={`graphie-${r.classroom_student_id}`}
                      defaultOpen={Boolean(recopieTexteId)}
                      fixedTexteId={recopieTexteId}
                      listTextes={async () => (await api.assignments.graphie.textes.query()) as GraphieTexteView[]}
                      load={async () =>
                        (await api.assignments.graphie.get.query({ assignmentId: id, classroomStudentId: r.classroom_student_id })) as GraphieAnalysisView | null
                      }
                      run={async (texteId) =>
                        (await api.assignments.graphie.run.mutate({ assignmentId: id, classroomStudentId: r.classroom_student_id, texteId })) as GraphieAnalysisView
                      }
                      review={async (review) =>
                        (await api.assignments.graphie.review.mutate({ assignmentId: id, classroomStudentId: r.classroom_student_id, review })) as GraphieAnalysisView
                      }
                      publish={async (published) => {
                        await api.assignments.graphie.publish.mutate({ assignmentId: id, classroomStudentId: r.classroom_student_id, published });
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface ParcoursResult {
  total_score: number;
  total_max: number;
  completed: boolean;
  tutor_notes: string;
  items: Array<{ title: string; score: number; max: number; done: boolean }>;
}

function ParcoursResultView({ assignmentId, classroomStudentId }: { assignmentId: string; classroomStudentId: string }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<ParcoursResult | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data === undefined) {
      setLoading(true);
      try {
        const r = await api.assignments.parcoursResult.query({ assignmentId, classroomStudentId });
        setData(r as ParcoursResult | null);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="w-full mt-1.5">
      <Button variant="secondary" size="sm" onClick={() => void toggle()}>
        <Sparkle size={13} weight="fill" className="mr-1.5" />
        {open ? 'Fermer le parcours' : 'Voir le parcours'}
      </Button>
      {open && (
        <div className="mt-2.5 pt-2.5 border-t border-border flex flex-col gap-2">
          {loading && <p className="text-[13px] text-muted-foreground">Chargement…</p>}
          {data === null && !loading && <p className="text-[13px] text-muted-foreground">Parcours non commencé.</p>}
          {data && (
            <>
              <div className="text-[13px] font-extrabold text-foreground tabular-nums">
                {data.total_score} / {data.total_max} {data.completed ? '· terminé' : '· en cours'}
              </div>
              {data.items.map((it, i) => (
                <div key={i} className="flex justify-between gap-2.5 text-[13px]">
                  <span className="text-foreground">{it.done ? '✓' : '·'} {it.title || `Question ${i + 1}`}</span>
                  <span className="font-bold tabular-nums shrink-0">{it.score}/{it.max}</span>
                </div>
              ))}
              {data.tutor_notes && (
                <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground bg-accent rounded-card px-3 py-2">
                  <strong className="text-foreground">Notes de Naïa :</strong> {data.tutor_notes}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
